import React, {useEffect, useRef, useMemo, useState} from 'react';
import Player from "@vimeo/player";
import {useResetRecoilState, useSetRecoilState} from "recoil";
import playerState from "../../state/ui/playerState";

const VimeoPlayer = ({src, sync, loop, provider, ...props}) => {
    const playerRef = useRef(null);
    const plyr = useRef(null);
    const setPlayerState = useSetRecoilState(playerState);
    const resetPlayerState = useResetRecoilState(playerState);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        plyr.current = new Player(playerRef.current, {
            id: src ||  487150670,
            muted: true,
            autoplay: true,
            playsinline: true,
        });
        console.log('DBG PLYR init', src ||  487150670)

        window.SpacePlayer = {
            requestFullscreen: () => plyr.current.requestFullscreen(),
            exitFullscreen: () => plyr.current.exitFullscreen(),
            mute: () => plyr.current.setVolume(0),
            unmute: () => plyr.current.setVolume(1),
            setQuality: (quality) => quality ? plyr.current.setQuality(quality) : new Promise(resolve => resolve()),
            setTrack: (track) => !track ? plyr.current.disableTextTrack() : plyr.current.enableTextTrack(track),
        };

        plyr.current.on('volumechange', e => {
            setPlayerState(old => ({
                ...old,
                muted: e.volume === 0,
            }));
        });

        plyr.current.on('fullscreenchange', e => {
            setPlayerState(old => ({
                ...old,
                fullscreen: e.fullscreen,
            }));
        });

        plyr.current.on('qualitychange', e => {
            setPlayerState(old => ({
                ...old,
                selectedQuality: e.quality,
            }))
        });

        plyr.current.on('texttrackchange', e => {
            setPlayerState(old => ({
                ...old,
                selectedTrack: e?.language || null,
            }))
        });

        plyr.current.on('loaded', () => {
            console.log('DBG PLYR ready')
            setReady(true);
            Promise.all([
                plyr.current.getMuted(),
                plyr.current.getFullscreen(),
                plyr.current.getQualities(),
                plyr.current.getTextTracks(),
            ]).then(([muted, fullscreen, qualities, tracks]) => {
                setPlayerState(old => ({
                    ...old,
                    muted,
                    fullscreen,
                    qualities: qualities.map(item => ({
                        id: item.id,
                        label: item.label,
                    })),
                    selectedQuality: qualities.find(i => i.active)?.id,
                    tracks: tracks.map(item => ({
                        id: item.language,
                        label: item.label,
                    })),
                    selectedTracks: tracks.find(i => i.mode === 'showing')?.language,
                }))
            });
        });

        plyr.current.on('loadedmetadata', (e) => {
            Promise.all([
                plyr.current.getQualities(),
                plyr.current.getTextTracks(),
            ]).then(([qualities, tracks]) => {
                setPlayerState(old => ({
                    ...old,
                    qualities: qualities.map(item => ({
                        id: item.id,
                        label: item.label,
                    })),
                    selectedQuality: qualities.find(i => i.active)?.id,
                    tracks: tracks.map(item => ({
                        id: item.language,
                        label: item.label,
                    })),
                    selectedTrack: tracks.find(i => i.mode === 'showing')?.language,
                }))
            });
        });

        return () => {
            plyr.current.destroy();
            delete window.SpacePlayer;

            return () => {
                resetPlayerState();
            }
        };
    }, []);

    useEffect(() => {
        if(!ready) {
            return;
        }

        (async () => {
            const currentId = await plyr.current.getVideoId();

            if(currentId != src) {
                const newId = plyr.current.loadVideo(src);
                console.log('DBG PLYR loadvideo', currentId, src, newId);
            }
        })();
    }, [src, ready]);

    useEffect(() => {
        if(!ready) {
            return;
        }
        console.log('DBG PLYR loop', !!loop);

        plyr.current.setLoop(!!loop);

        if(loop) {
            plyr.current.getPaused().then(function(paused) {
                if(paused) {
                    plyr.current.play();
                }
            });
        }
    }, [loop, ready]);

    useEffect(() => {
        if(!ready) {
            return;
        }

        let onPlay = undefined;

        if (sync) {
            const syncDate = +(new Date(sync));

            onPlay = (data) => {
                const currentTime = +(new Date);
                const actualSeconds = data.duration ? Math.min(data.duration, (currentTime - syncDate) / 1000) : (currentTime - syncDate) / 1000;

                console.log('DBG PLYR re-sync', actualSeconds > 0 && Math.abs((data.seconds || 0) - actualSeconds) > 5, actualSeconds);

                if(actualSeconds > 0 && Math.abs((data.seconds || 0) - actualSeconds) > 5) {
                    plyr.current.setCurrentTime(actualSeconds);
                }
            };

            plyr.current.getDuration().then(duration => {
                onPlay({
                    duration,
                    seconds: 0,
                });
            });

            plyr.current.on("play", onPlay);
            plyr.current.on("seeked", onPlay);
        } else {
            plyr.current.off("play");
            plyr.current.off("seeked");
        }

        return () => {
            plyr.current && plyr.current.off("play", onPlay);
            plyr.current && plyr.current.off("seeked", onPlay);
        };
    }, [sync, ready]);

    return <div id="vimeo" ref={playerRef} {...props} />
};


export default VimeoPlayer;
